// Build Your Crew — add who you want, as many as you want, get one card.
//
// There are no jobs to fill. Nobody has to be a doctor or a sniper: you add
// members, between two and ten, and the card grows with them — fewer members
// make a shorter, tighter card, ten make a tall one.
//
// Each member carries their own bounty on the card, from the archive, and the
// total underneath is the sum. The preview IS the download: everything is
// drawn once into a 1080-wide canvas and the screen shows it scaled down.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import characterProfiles from '../data/characterProfiles';
import animeConfig from '../config/animeConfig';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { crewBuilderText } from '../components/QuizPageText/quizPageText.data';
import { trackCrewAdd, trackCrewFinish, trackCrewAction } from '../services/analyticsService';
import './CrewBuilder.css';

const W = 1080;
const MAX = 10;

// "Buggy the Clown" is known as Buggy, not as Clown.
const shortName = (full) => {
  const name = full.trim();
  const m = name.match(/^(.+?)\s+the\s+/i);
  return (m ? m[1] : name).split(/\s+/).pop();
};

const CAST = characterProfiles
  .filter((c) => c.image && c.name)
  .map((c) => ({
    slug: c.slug,
    name: c.name,
    short: shortName(c.name),
    crew: c.crew || '',
    image: c.image,
    bounty: Number(String(c.bounty || '').replace(/[^0-9]/g, '')) || 0,
  }));

const bySlug = {};
CAST.forEach((c) => { bySlug[c.slug] = c; });

const money = (n) => n.toLocaleString('en-US');
const berry = (n) => (n ? `฿${money(n)}` : 'No bounty');

const joinsOrRtl = (text) => /[֐-ࣿ‏יִ-﷿ﹰ-﻿]/.test(text);

const CrewBuilder = () => {
  const canvasRef = useRef(null);
  const imagesRef = useRef({});

  // The crew is kept on this device, so leaving and coming back finds it as it was.
  const saved = (() => { try { return JSON.parse(localStorage.getItem('opq-crew')) || {}; } catch (e) { return {}; } })();
  const [crewName, setCrewName] = useState(saved.name || '');
  const [members, setMembers] = useState(Array.isArray(saved.members) ? saved.members.slice(0, MAX) : []);     // slugs, in order; the first is the captain
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const typed = crewName.trim();
  const displayName = typed
    ? (/[a-z]/i.test(typed) && !/[֐-ࣿ]/.test(typed) ? typed.toUpperCase() : typed)
    : 'THE STRAW HAT PIRATES';

  const crew = members.map((slug) => bySlug[slug]).filter(Boolean);
  const total = crew.reduce((sum, c) => sum + c.bounty, 0);

  // --- the card's shape follows the crew's size -----------------------------
  const count = Math.max(crew.length, 1);
  const rows = Math.ceil(count / 2);
  const big = count <= 4;
  const cellH = big ? 310 : 292; // room for the name AND the bounty under the last row
  const HEAD = 265;
  const FOOT = 225;
  const H = HEAD + rows * cellH + FOOT;

  const getImage = useCallback((src) => {
    if (!src) return null;
    const cache = imagesRef.current;
    if (cache[src]) return cache[src].complete && cache[src].naturalWidth ? cache[src] : null;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setTick((t) => t + 1);
    img.onerror = () => setTick((t) => t + 1);
    img.src = src;
    cache[src] = img;
    return null;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    const ink = '#2a1c0c';

    const paper = ctx.createLinearGradient(0, 0, W, H);
    paper.addColorStop(0, '#efe0bb');
    paper.addColorStop(0.5, '#e7d4a8');
    paper.addColorStop(1, '#dcc695');
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, W, H);

    ctx.save();
    ctx.globalAlpha = 0.07;
    ctx.fillStyle = '#6b4a1d';
    [[150, 230, 120], [900, H * 0.35, 150], [260, H - 220, 130], [820, H - 150, 110]].forEach(([x, y, r]) => {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

    ctx.strokeStyle = 'rgba(42, 28, 12, 0.55)';
    ctx.lineWidth = 8;
    ctx.strokeRect(26, 26, W - 52, H - 52);
    ctx.lineWidth = 2;
    ctx.strokeRect(46, 46, W - 92, H - 92);

    const centre = (text, y, font, color = ink, spacing = 0) => {
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      if (!spacing || joinsOrRtl(text)) { ctx.fillText(text, W / 2, y); return; }
      const chars = [...text];
      const width = chars.reduce((w, ch) => w + ctx.measureText(ch).width + spacing, -spacing);
      let x = W / 2 - width / 2;
      chars.forEach((ch) => {
        ctx.textAlign = 'left';
        ctx.fillText(ch, x, y);
        x += ctx.measureText(ch).width + spacing;
      });
      ctx.textAlign = 'center';
    };

    centre('THE CREW OF', 128, '600 34px Georgia, serif', 'rgba(42,28,12,0.7)', 7);

    let nameSize = 76;
    ctx.font = `bold ${nameSize}px Georgia, serif`;
    while (ctx.measureText(displayName).width > W - 160 && nameSize > 34) {
      nameSize -= 2;
      ctx.font = `bold ${nameSize}px Georgia, serif`;
    }
    centre(displayName, 203, `bold ${nameSize}px Georgia, serif`, ink);

    ctx.strokeStyle = 'rgba(42, 28, 12, 0.45)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(200, 230);
    ctx.lineTo(W - 200, 230);
    ctx.stroke();

    const cells = crew.length ? crew : [null];
    cells.forEach((member, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const cx = 300 + col * 480;
      const cy = HEAD + row * cellH + (big ? 115 : 100);
      const r = big ? 98 : 84;
      const img = member ? getImage(member.image) : null;

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (img) {
        const scale = Math.max((r * 2) / img.width, (r * 2) / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, cx - dw / 2, cy - dh / 2 - r * 0.12, dw, dh);
      } else {
        ctx.fillStyle = 'rgba(42, 28, 12, 0.08)';
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
        ctx.fillStyle = 'rgba(42, 28, 12, 0.3)';
        ctx.font = `bold ${r}px Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.fillText('?', cx, cy + r * 0.35);
      }
      ctx.restore();

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = member ? 'rgba(42,28,12,0.75)' : 'rgba(42,28,12,0.3)';
      ctx.lineWidth = member ? 6 : 3;
      ctx.stroke();

      ctx.textAlign = 'center';

      // The captain is the one who gets a title; everyone else is just crew.
      if (member && i === 0) {
        ctx.fillStyle = 'rgba(42,28,12,0.62)';
        ctx.font = '600 22px Georgia, serif';
        ctx.fillText('CAPTAIN', cx, cy - r - 14);
      }

      const nameY = cy + r + 52;
      ctx.fillStyle = ink;
      let s = big ? 36 : 32;
      const label = member ? member.name : 'Add your crew';
      ctx.font = `bold ${s}px Georgia, serif`;
      while (ctx.measureText(label).width > 430 && s > 20) {
        s -= 1;
        ctx.font = `bold ${s}px Georgia, serif`;
      }
      ctx.fillText(label, cx, nameY);

      // What they are worth, under their name — the whole point of the card.
      if (member) {
        if (member.bounty) berryLine(member.bounty, cx, nameY + (big ? 40 : 36), big ? 29 : 26, 'rgba(42,28,12,0.72)', false);
        else {
          ctx.fillStyle = 'rgba(42,28,12,0.72)';
          ctx.font = `600 ${big ? 29 : 26}px Georgia, serif`;
          ctx.fillText('No bounty', cx, nameY + (big ? 40 : 36));
        }
      }
    });

    // The berry sign drawn by hand in the digits' own font: Georgia has no ฿,
    // so the browser borrowed one from another font that sat off the line.
    function berryLine(n, cx, y, size, color, bold) {
      const font = `${bold ? 'bold' : '600'} ${size}px Georgia, serif`;
      ctx.font = font;
      const digits = money(n);
      const bW = ctx.measureText('B').width;
      const gap = size * 0.12;
      const full = bW + gap + ctx.measureText(digits).width;
      const left = cx - full / 2;
      ctx.save();
      ctx.textAlign = 'left';
      ctx.fillStyle = color;
      ctx.fillText('B', left, y);
      ctx.fillText(digits, left + bW + gap, y);
      // the stroke through the B, top to bottom of the digits
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, size * 0.075);
      ctx.beginPath();
      ctx.moveTo(left + bW * 0.42, y - size * 0.86);
      ctx.lineTo(left + bW * 0.42, y + size * 0.14);
      ctx.stroke();
      ctx.restore();
    }

    const footTop = HEAD + rows * cellH + 10;
    ctx.strokeStyle = 'rgba(42, 28, 12, 0.45)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(200, footTop);
    ctx.lineTo(W - 200, footTop);
    ctx.stroke();

    centre(
      crew.length ? `TOTAL BOUNTY · ${crew.length} MEMBER${crew.length === 1 ? '' : 'S'}` : 'TOTAL BOUNTY',
      footTop + 52, '600 26px Georgia, serif', 'rgba(42,28,12,0.7)', 5,
    );
    berryLine(total, W / 2, footTop + 112, 56, ink, true);
    centre('onepiecetrivia.com', footTop + 160, '500 22px Georgia, serif', 'rgba(42,28,12,0.55)', 3);
  }, [displayName, crew, total, rows, big, cellH, H, getImage]);

  useEffect(() => { draw(); }, [draw, tick]);
  useEffect(() => {
    try { localStorage.setItem('opq-crew', JSON.stringify({ name: crewName, members })); } catch (e) { /* private mode */ }
  }, [crewName, members]);

  // --- actions -------------------------------------------------------------
  const add = (slug) => {
    if (members.length >= MAX || members.includes(slug)) return;
    const c = bySlug[slug];
    if (c) trackCrewAdd(c.name, c.bounty, members.length + 1);
    setMembers((m) => (m.includes(slug) ? m : [...m, slug]));
    setPicking(false);
    setQuery('');
  };

  const remove = (slug) => setMembers((m) => m.filter((s) => s !== slug));

  const move = (index, delta) => {
    setMembers((m) => {
      const next = [...m];
      const to = index + delta;
      if (to < 0 || to >= next.length) return m;
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  const crewFacts = () => ({
    size: crew.length,
    total,
    names: crew.map((c) => c.name).join(', ').slice(0, 100),
    named: !!typed,
  });

  const randomCrew = () => {
    trackCrewAction('random');
    const size = members.length >= 2 ? members.length : 6;
    const pool = [...CAST].sort(() => Math.random() - 0.5).slice(0, size);
    setMembers(pool.map((c) => c.slug));
  };

  const download = async () => {
    setBusy(true);
    try {
      draw();
      const url = canvasRef.current.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(typed || 'my-one-piece-crew').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
      a.click();
      trackCrewFinish('download', crewFacts());
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const text = `My One Piece crew is worth ฿${money(total)}. Build yours at https://www.onepiecetrivia.com/crew`;
    try {
      draw();
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'my-one-piece-crew.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else if (navigator.share) {
        await navigator.share({ title: 'My One Piece crew', text });
      } else {
        await navigator.clipboard.writeText(text);
      }
      trackCrewFinish('share', crewFacts());
    } catch (e) { /* the person closed the share sheet */ }
  };

  const q = query.trim().toLowerCase();
  const matches = (q
    ? CAST.filter((c) => c.name.toLowerCase().includes(q) || c.crew.toLowerCase().includes(q))
    : CAST
  ).filter((c) => !members.includes(c.slug));

  return (
    <>
      <Helmet>
        <title>Build Your One Piece Crew — Pick Your Pirates and Download the Card</title>
        <meta name="description" content="Pick up to ten One Piece characters, see what each one is worth and what your whole crew is worth, then download the card as an image. Free, no signup." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/crew" />
        <meta property="og:title" content="Build Your One Piece Crew" />
        <meta property="og:description" content="Your pirates, their bounties, and the total your crew is worth — on one card you can keep." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/crew" />
        <meta property="og:image" content="https://www.onepiecetrivia.com/og-crew.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Build Your One Piece Crew" />
        <meta name="twitter:description" content="Your pirates, their bounties, and the total your crew is worth — on one card you can keep." />
        <meta name="twitter:image" content="https://www.onepiecetrivia.com/og-crew.png" />
      </Helmet>

      <div
        className="cb-wrapper"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0.9)), url(${animeConfig.onePiece.backgroundImage})`,
        }}
      >
        <div className="cb-page">
          <h1 className="cb-title">Build Your One Piece Crew</h1>
          <p className="cb-sub">Anyone you want, up to ten. Every bounty is real, and so is the total.</p>

          <div className="cb-layout">
            <div className="cb-stage">
              <canvas ref={canvasRef} className="cb-canvas" />
              <div className="cb-actions">
                <button type="button" className="cb-btn cb-btn-primary" onClick={download} disabled={busy || !crew.length}>
                  {busy ? 'Making it…' : 'Download'}
                </button>
                <button type="button" className="cb-btn" onClick={share} disabled={!crew.length}>Share</button>
              </div>
            </div>

            <div className="cb-controls">
              <label className="cb-field">
                <span>Crew name</span>
                <input
                  className="cb-input"
                  maxLength={26}
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value)}
                  placeholder="The Straw Hat Pirates"
                />
              </label>

              <div className="cb-field">
                <span>Your crew <em>{crew.length}/{MAX}</em></span>

                <ul className="cb-list">
                  {crew.map((c, i) => (
                    <li key={c.slug} className="cb-member">
                      <img src={c.image} alt="" loading="lazy" />
                      <span className="cb-member-text">
                        <strong>{c.name}</strong>
                        <small>{i === 0 ? 'Captain · ' : ''}{berry(c.bounty)}</small>
                      </span>
                      <span className="cb-member-tools">
                        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                        <button type="button" onClick={() => move(i, 1)} disabled={i === crew.length - 1} aria-label="Move down">↓</button>
                        <button type="button" className="cb-drop" onClick={() => remove(c.slug)} aria-label={`Remove ${c.name}`}>×</button>
                      </span>
                    </li>
                  ))}
                </ul>

                {crew.length < MAX ? (
                  <button type="button" className="cb-add" onClick={() => { setPicking(true); setQuery(''); }}>
                    <i className="fa fa-plus" /> Add a member
                  </button>
                ) : (
                  <p className="cb-note">Ten is the whole crew. Drop someone to add another.</p>
                )}
              </div>

              <div className="cb-row">
                <button type="button" className="cb-btn cb-btn-ghost" onClick={randomCrew}>Surprise me</button>
                <button type="button" className="cb-btn cb-btn-ghost" onClick={() => { trackCrewAction('clear'); setMembers([]); }}>Clear</button>
              </div>

              <p className="cb-total">
                Total bounty <strong>&#3647;{money(total)}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>

      {picking && (
        <div className="cb-sheet" role="dialog" aria-modal="true">
          <div className="cb-sheet-head">
            <strong>Add to your crew</strong>
            <button type="button" className="cb-close" onClick={() => setPicking(false)} aria-label="Close">×</button>
          </div>
          <input
            className="cb-input cb-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${CAST.length} characters…`}
            autoFocus
          />
          <div className="cb-grid">
            {matches.map((c) => (
              <button key={c.slug} type="button" className="cb-pick" onClick={() => add(c.slug)}>
                <img src={c.image} alt="" loading="lazy" />
                <span>{c.short}</span>
                <small>{c.bounty ? `฿${money(c.bounty)}` : '—'}</small>
              </button>
            ))}
            {matches.length === 0 && <p className="cb-none">Nobody left by that name.</p>}
          </div>
        </div>
      )}

      <QuizPageText {...crewBuilderText} />
    </>
  );
};

export default CrewBuilder;
